import React from "react";
import type { ReactElement } from "react";

interface FilterPanelContextValue {
  readonly isOpen: boolean;
  readonly open: () => void;
  readonly close: () => void;
  readonly toggle: () => void;
}

const FilterPanelContext = React.createContext<FilterPanelContextValue | null>(null);

export function FilterPanelProvider({ children }: { readonly children: React.ReactNode }): ReactElement {
  const [isOpen, setIsOpen] = React.useState(false);

  const value = React.useMemo<FilterPanelContextValue>(
    () => ({
      isOpen,
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
      toggle: () => setIsOpen((prev) => !prev),
    }),
    [isOpen],
  );

  return <FilterPanelContext.Provider value={value}>{children}</FilterPanelContext.Provider>;
}

export function useFilterPanel(): FilterPanelContextValue {
  const context = React.useContext(FilterPanelContext);
  if (!context) {
    // Return a no-op implementation when context is not available
    return {
      isOpen: false,
      open: () => {},
      close: () => {},
      toggle: () => {},
    };
  }
  return context;
}
